import React, { useCallback, useEffect, useRef, useState } from 'react';
import Toast from './Toast';
import { withTimeout } from '../shared/lib/withTimeout';

const POLL_INTERVAL_MS = 10_000;
const HEALTH_FETCH_TIMEOUT_MS = 8_000;
const FAILURES_BEFORE_TOAST = 5;

const OUTAGE_MESSAGE = 'There is an issue and we are working to resolve it.';

function getHealthUrl(): string {
  const base = (process.env.REACT_APP_API_URL ?? '').replace(/\/$/, '');
  return base ? `${base}/api/health` : '/api/health';
}

async function fetchHealthOk(): Promise<boolean> {
  const url = getHealthUrl();
  const res = await withTimeout(
    fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { 'X-Service': 'bmcs' },
    }),
    HEALTH_FETCH_TIMEOUT_MS,
    'Health check timeout'
  );
  if (!res.ok) return false;
  const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
  return data?.ok === true;
}

/**
 * Polls the API health endpoint. After 5 consecutive failures, shows a top-right toast.
 * Success clears the failure streak and hides the toast; dismiss hides until the API is healthy again.
 */
const ApiHealthMonitor: React.FC = () => {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const dismissedUntilHealthyRef = useRef(false);

  const runCheck = useCallback(async () => {
    try {
      const ok = await fetchHealthOk();
      if (ok) {
        consecutiveFailuresRef.current = 0;
        dismissedUntilHealthyRef.current = false;
        setToastMessage(null);
        return;
      }
    } catch (err) {
      console.warn('API health check failed', err);
    }

    consecutiveFailuresRef.current += 1;
    if (
      consecutiveFailuresRef.current >= FAILURES_BEFORE_TOAST &&
      !dismissedUntilHealthyRef.current
    ) {
      setToastMessage(OUTAGE_MESSAGE);
    }
  }, []);

  useEffect(() => {
    void runCheck();
    const id = window.setInterval(() => {
      void runCheck();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [runCheck]);

  const onDismiss = useCallback(() => {
    setToastMessage(null);
    dismissedUntilHealthyRef.current = true;
  }, []);

  return (
    <Toast
      message={toastMessage}
      onDismiss={onDismiss}
      variant="error"
      autoCloseMs={0}
      position="top-right"
    />
  );
};

export default ApiHealthMonitor;
